import { useEffect, useMemo, useState } from "react";

import { ValueSetExpansionContains } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { ComboboxItem, Loader, Select, SelectProps } from "@mantine/core";

import * as Sentry from "@sentry/react";

import { getValueSetOptions } from "@/utils/medplum";
import { showSystemErrorNotification } from "@/utils/notifications";

const UNAVAILABLE_OPTIONS_MESSAGE = "Options unavailable. Please try again.";

export interface ValueSetSelectProps extends Omit<SelectProps, "value" | "onChange"> {
  answerValueSet: string;
  value?: ValueSetExpansionContains | null;
  onChange?: (value: ValueSetExpansionContains | null, option: ComboboxItem) => void;
}

export default function ValueSetSelect({
  answerValueSet,
  placeholder = "Select",
  rightSection,
  onChange,
  value,
  ...props
}: ValueSetSelectProps) {
  const [valueSetOptions, setValueSetOptions] = useState<ValueSetExpansionContains[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const medplum = useMedplum();

  const stringifiedValue = useMemo(() => (value ? JSON.stringify(value) : null), [value]);

  const resolvedValue = useMemo(() => {
    if (!stringifiedValue || !valueSetOptions.length) return stringifiedValue;
    const matchesOption = valueSetOptions.some((opt) => JSON.stringify(opt) === stringifiedValue);
    if (matchesOption) return stringifiedValue;
    const matchByCodeOrDisplay = valueSetOptions.find(
      (opt) => (value && opt.code === value.code) || (value && opt.display === value.display),
    );
    return matchByCodeOrDisplay ? JSON.stringify(matchByCodeOrDisplay) : stringifiedValue;
  }, [stringifiedValue, value, valueSetOptions]);

  useEffect(() => {
    let isCurrent = true;

    const getValueSet = async () => {
      setIsLoading(true);
      setHasError(false);

      try {
        const valueSetElements = await getValueSetOptions(medplum, answerValueSet);
        if (!isCurrent) return;

        if (valueSetElements?.length) {
          setValueSetOptions(valueSetElements);
        } else {
          throw new Error(
            `[ValueSetSelect] ValueSet is either empty or does not exist: ${answerValueSet}`,
          );
        }
      } catch (error) {
        if (!isCurrent) return;

        setValueSetOptions([]);
        setHasError(true);
        showSystemErrorNotification();
        Sentry.captureException(error);
      } finally {
        if (isCurrent) setIsLoading(false);
      }
    };

    getValueSet();
    return () => {
      isCurrent = false;
    };
  }, [answerValueSet, medplum]);

  const handleChange = (value: string | null, option: ComboboxItem) => {
    if (!onChange) return;

    if (value) {
      onChange(JSON.parse(value), option);
    } else {
      onChange(null, option);
    }
  };

  return (
    <Select
      {...props}
      searchable
      value={resolvedValue}
      onChange={handleChange}
      disabled={props.disabled || isLoading || hasError}
      placeholder={isLoading ? "Loading..." : placeholder}
      error={hasError ? UNAVAILABLE_OPTIONS_MESSAGE : props.error}
      rightSection={isLoading ? <Loader size="xs" /> : rightSection}
      data={valueSetOptions.map((option) => ({
        value: JSON.stringify(option),
        label: option.display || option.code || "",
      }))}
    />
  );
}
